import { Injectable } from '@angular/core';
import { CussService } from '@services/cuss.service';
import { DeviceInt, DeviceReponseInt } from '@app/interfaces/device.interface';
import { deviceIds, Device } from 'src/embross-device-manager';
import { validatePNR, BarcodeRawData, BarcodeResultPayload } from '@utils/index';
import { Observable, Subject } from 'rxjs';
import { NewRelicEventsService } from '@app/services/new-relic-events.service';
import {
  NEW_RELIC_EVENT,
  SCAN_RESULT,
  SCAN_ERROR_TYPE,
  BOARDING_PASS_TYPE,
} from '@app/enums/new-relic.enum';

@Injectable({
  providedIn: 'root',
})
export class BarcodeDeviceService implements DeviceInt {
  device: Device;
  listen$: Subject<any> = new Subject<any>();
  isComplete = false;
  timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;

  //Marca de tiempo al invocar enable() para calcular duración del escaneo
  private _scanStart: number | null = null;

  constructor(private cussService: CussService, private _newRelicEvents: NewRelicEventsService) {
    this.setDevice();
  }

  private setDevice() {
    if (this.cussService.getDeviceManager()) {
      this.device = this.cussService.getDeviceManager().getDevice(deviceIds.BARCODE_READER);
    }
  }

  disable(): void {
    if (this.device) {
      this.device.disable();
      this.device.OnDeviceEvent = null;
    }
    if (this.timeoutId !== undefined) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    this._scanStart = null;
  }

  enable(): void {
    this.setDevice();
    if (this.device) {
      try {
        this.device.OnDeviceEvent = this.callback;
        this.device.enable();
        this._scanStart = Date.now();
      } catch (error) {
        console.error('[Error enable barcode]', error);
      }
    }
  }

  listen(): Observable<any> {
    if (this.isComplete || this.listen$.closed) {
      this.listen$ = new Subject<any>();
      this.isComplete = false;
    }
    return this.listen$.asObservable();
  }

  private ensureListenSubjectOpen(): void {
    if (this.listen$.closed) {
      this.listen$ = new Subject<any>();
      this.isComplete = false;
    }
  }

  private emitListen(payload: unknown): void {
    try {
      this.listen$.next(payload);
    } catch (err) {
      console.error('[BarcodeDeviceService] listen$ subscriber error', err);
    }
  }

  private emitPendingThen(finalPayload: unknown): void {
    this.ensureListenSubjectOpen();
    this.emitListen({ barcodeParsePending: true });
    queueMicrotask(() => this.emitListen(finalPayload));
  }

  readDummy() {
    if (this.timeoutId !== undefined) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    this._scanStart = Date.now();
    this.timeoutId = setTimeout(() => {
      this.emitScanMetric(SCAN_RESULT.SUCCESS, SCAN_ERROR_TYPE.NONE);
      this.emitPendingThen({
        pnr: 'SUHFDZ',
        lastName: null,
        firstName: null,
      } as BarcodeResultPayload);
    }, 1500);
  }

  callback = (event: DeviceReponseInt) => {
    switch (event.key) {
      case 'parsedBarcodeData': {
        let pnr: string | null = null;
        let lastName: string | null = null;
        let firstName: string | null = null;
        let isValid = false;
        try {
          const response = event.value;
          if (typeof response !== 'string' || response.trim() === '') {
            this.emitScanMetric(SCAN_RESULT.FAILURE, SCAN_ERROR_TYPE.BARCODE_UNREADABLE);
            this.emitPendingThen({ invalidBarcode: true });
            break;
          }
          const data = JSON.parse(response) as BarcodeRawData;
          if (data?.legs?.length) {
            const raw = data.legs[0]?.pnr;
            pnr = raw ? String(raw).toUpperCase().replace(/\s/g, '') : null;
            if (pnr && validatePNR(pnr)) {
              isValid = true;
            }
          }
          lastName = data?.passengerLastName?.trim().toUpperCase() || null;
          firstName = data?.passengerFirstName?.trim().toUpperCase() || null;
        } catch {
          this.emitScanMetric(SCAN_RESULT.FAILURE, SCAN_ERROR_TYPE.FORMAT_NOT_RECOGNIZED);
          this.emitPendingThen({ invalidBarcode: true });
          break;
        }
        if (isValid && pnr) {
          this.emitScanMetric(SCAN_RESULT.SUCCESS, SCAN_ERROR_TYPE.NONE);
          this.emitPendingThen({ pnr, lastName, firstName } as BarcodeResultPayload);
        } else {
          this.emitScanMetric(SCAN_RESULT.FAILURE, SCAN_ERROR_TYPE.FORMAT_NOT_RECOGNIZED);
          this.emitPendingThen({ invalidBarcode: true });
        }

        break;
      }
      case 'barcodeDamaged': {
        this.emitScanMetric(SCAN_RESULT.FAILURE, SCAN_ERROR_TYPE.BARCODE_UNREADABLE);
        this.emitPendingThen({ invalidBarcode: true });
        break;
      }
      case 'barcodeRemoved':
        break;
    }
  };

  private getElapsedMs(): number {
    if (!this._scanStart) return 0;
    return Date.now() - this._scanStart;
  }

  private emitScanMetric(scanResult: SCAN_RESULT, errorType: SCAN_ERROR_TYPE): void {
    try {
      this._newRelicEvents.trackEvent(NEW_RELIC_EVENT.BOARDING_PASS_SCANNER_RESULT, {
        scan_result: scanResult,
        scan_duration_ms: this.getElapsedMs(),
        error_type: errorType,
        boarding_pass_type: BOARDING_PASS_TYPE.BARCODE,
      });
    } catch (e) {
      console.warn('[BarcodeDeviceService] Error emitting scan metric', e);
    }
  }
}
